import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Search, BookOpen, ExternalLink, Loader2, Clipboard, Trash2, Plus, FileText, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function CiteBtn({ citation }) {
  const copy = () => {
    const quote = `«${citation.text.replace(/^«|»$/g, "")}» — ${citation.author ? citation.author + ", " : ""}${citation.book_title}${citation.page ? ", p. " + Math.floor(citation.page) : ""}`;
    navigator.clipboard.writeText(quote);
    localStorage.setItem("library_quote_pending", JSON.stringify({ quote, at: Date.now() }));
    toast.success("Cita copiada. Abrí el editor y pegá o aparecerá automáticamente al guardar.");
  };
  return (
    <button onClick={copy} className="btn-ghost !min-h-8 !px-2 text-xs" data-testid={`btn-cite-${citation.item_id}-${citation.page}`}>
      <Clipboard size={13} /> Insertar cita
    </button>
  );
}

function ItemCard({ item, onDelete }) {
  return (
    <article className="card-warm p-5 flex flex-col gap-2 h-full" data-testid={`lib-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="eyebrow mb-1">{item.author || "Sin autor"}</div>
          <h3 className="font-serif text-lg font-semibold text-[#13302A] leading-tight">{item.title}</h3>
        </div>
        <div className="flex gap-1">
          {item.kind === "classic" ? (
            <span className="badge-soft">Clásico</span>
          ) : item.kind === "personal" ? (
            <span className="badge-gold">Mío</span>
          ) : (
            <span className="badge-soft">Enlace</span>
          )}
        </div>
      </div>
      {item.description && <p className="text-sm text-[#52635C] mt-1 line-clamp-3">{item.description}</p>}
      <div className="flex flex-wrap gap-1 mt-2">
        {(item.topics || []).slice(0, 4).map((t) => (<span key={t} className="text-xs px-2 py-0.5 rounded-full bg-[#F1F5F3] text-[#1F4A40]">{t}</span>))}
      </div>
      {item.copyright_note && (
        <div className="text-xs text-[#8B9995] italic mt-1" data-testid={`copyright-${item.id}`}>{item.copyright_note}</div>
      )}
      <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
        {item.external_url && (
          <a href={item.external_url} target="_blank" rel="noreferrer" className="btn-ghost !min-h-9 !px-3 text-sm" data-testid={`btn-external-${item.id}`}>
            <ExternalLink size={14} /> {item.source_name || "Abrir"}
          </a>
        )}
        {item.file_url && (
          <a href={item.file_url} target="_blank" rel="noreferrer" className="btn-ghost !min-h-9 !px-3 text-sm" data-testid={`btn-file-${item.id}`}>
            <FileText size={14} /> Abrir archivo
          </a>
        )}
        {item.kind === "personal" && (
          <button onClick={() => onDelete(item.id)} className="btn-ghost !min-h-9 ml-auto" aria-label="Eliminar"><Trash2 size={14} /></button>
        )}
      </div>
    </article>
  );
}

export default function StudyLibrary() {
  const [classics, setClassics] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  // AI search
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [citations, setCitations] = useState([]);
  const [searching, setSearching] = useState(false);

  // Upload form
  const [upload, setUpload] = useState({ title: "", author: "", topics: "", bible_books: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [all, lk] = await Promise.all([
        api.get("/admin/library/items"),
        api.get("/admin/library/links"),
      ]);
      setClassics(all.data.filter((i) => i.kind === "classic"));
      setPersonal(all.data.filter((i) => i.kind === "personal"));
      setLinks(lk.data);
    } catch (e) {
      toast.error("No se pudo cargar la biblioteca");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const search = async () => {
    if (!question.trim()) return;
    setSearching(true); setAnswer(""); setCitations([]);
    try {
      const r = await api.post("/admin/library/search", { question });
      setAnswer(r.data.answer);
      setCitations(r.data.citations);
    } catch (e) {
      toast.error("No pudimos buscar en este momento");
    } finally { setSearching(false); }
  };

  const doUpload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Elegí un archivo PDF o EPUB"); return; }
    if (!upload.title.trim()) { toast.error("Agregá un título"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", upload.title);
      fd.append("author", upload.author);
      fd.append("topics", upload.topics);
      fd.append("bible_books", upload.bible_books);
      const r = await api.post("/admin/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Subido · ${r.data.chunk_count} páginas indexadas`);
      setUpload({ title: "", author: "", topics: "", bible_books: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("¿Eliminar este libro de tu biblioteca?")) return;
    try { await api.delete(`/admin/library/items/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="library-admin-page">
      <Link to="/estudio" className="btn-ghost !min-h-10 mb-6"><ArrowLeft size={16} /> Volver al estudio</Link>
      <div className="eyebrow mb-2">Biblioteca del Pastor</div>
      <h1 className="font-serif text-3xl md:text-5xl font-bold text-[#13302A] mb-2">Clásicos reformados · Tus libros · Búsqueda con IA</h1>
      <p className="text-[#52635C] mb-8">Obras de dominio público, tus propios PDF/ePub y respuestas pastorales citadas por tu biblioteca.</p>

      <Tabs defaultValue="search">
        <TabsList className="bg-white border border-[rgba(31,74,64,0.1)]">
          <TabsTrigger value="search" data-testid="tab-ai-search"><Sparkles size={14} className="mr-1" /> Búsqueda IA</TabsTrigger>
          <TabsTrigger value="classics" data-testid="tab-classics"><BookOpen size={14} className="mr-1" /> Clásicos</TabsTrigger>
          <TabsTrigger value="personal" data-testid="tab-personal"><FileText size={14} className="mr-1" /> Mis libros</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links"><ExternalLink size={14} className="mr-1" /> Enlaces</TabsTrigger>
        </TabsList>

        {/* AI SEARCH */}
        <TabsContent value="search" className="mt-5">
          <div className="card-warm p-5 mb-5">
            <div className="flex gap-2">
              <input
                className="input-warm"
                placeholder="Ej: ¿qué dicen mis comentarios sobre Mateo 14:22-33?"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                data-testid="ai-question-input"
              />
              <button onClick={search} disabled={searching} className="btn-primary !min-h-12" data-testid="btn-ai-search">
                {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Buscar
              </button>
            </div>
            <p className="text-xs text-[#8B9995] mt-2">La IA responde sólo con fragmentos de tu biblioteca (tus libros + clásicos con texto cargado). Cada respuesta cita autor, libro y página.</p>
          </div>

          {answer && (
            <div className="card-warm p-5 mb-5" data-testid="ai-answer-card">
              <div className="eyebrow mb-2">Respuesta pastoral</div>
              <p className="font-serif text-[17px] leading-relaxed text-[#1C2421] whitespace-pre-wrap">{answer}</p>
            </div>
          )}

          {citations.length > 0 && (
            <div className="space-y-3">
              <div className="eyebrow">Fragmentos citados</div>
              {citations.map((c, i) => (
                <div key={i} className="card-warm p-4" data-testid={`citation-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-mono text-xs text-[#1F4A40]">
                      {c.author ? c.author + " · " : ""}<strong>{c.book_title}</strong>{c.page ? ", p. " + Math.floor(c.page) : ""}
                    </div>
                    <CiteBtn citation={c} />
                  </div>
                  <p className="font-serif text-[15px] leading-relaxed text-[#1C2421]">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CLASSICS */}
        <TabsContent value="classics" className="mt-5">
          <p className="text-sm text-[#52635C] mb-4">
            Obras clásicas reformadas. Mostramos sólo texto cuando la traducción al español es de dominio público verificable; en caso contrario abrimos la obra en una fuente externa confiable.
          </p>
          {loading ? <div className="text-[#52635C]">Cargando…</div> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {classics.map((it) => <ItemCard key={it.id} item={it} onDelete={removeItem} />)}
            </div>
          )}
        </TabsContent>

        {/* PERSONAL */}
        <TabsContent value="personal" className="mt-5">
          <div className="card-warm p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Upload size={16} className="text-[#1F4A40]" />
              <h3 className="font-serif text-lg font-semibold text-[#13302A]">Subir un libro (PDF o EPUB)</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <input className="input-warm" placeholder="Título" value={upload.title} onChange={(e) => setUpload({ ...upload, title: e.target.value })} data-testid="up-title" />
              <input className="input-warm" placeholder="Autor (ej: John MacArthur)" value={upload.author} onChange={(e) => setUpload({ ...upload, author: e.target.value })} data-testid="up-author" />
              <input className="input-warm" placeholder="Temas (coma separados: soteriología, santificación)" value={upload.topics} onChange={(e) => setUpload({ ...upload, topics: e.target.value })} data-testid="up-topics" />
              <input className="input-warm" placeholder="Libros bíblicos (coma: Romanos, Juan)" value={upload.bible_books} onChange={(e) => setUpload({ ...upload, bible_books: e.target.value })} data-testid="up-books" />
            </div>
            <div className="flex items-center gap-3 mt-3">
              <input ref={fileRef} type="file" accept=".pdf,.epub" className="text-sm" data-testid="up-file" />
              <button onClick={doUpload} disabled={uploading} className="btn-gold" data-testid="btn-upload-book">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Subir e indexar
              </button>
            </div>
            <p className="text-xs text-[#8B9995] mt-2">Subí sólo libros que ya comprás. Son privados: nadie más los ve. Se extrae el texto y las referencias bíblicas para búsquedas con IA.</p>
          </div>
          {loading ? <div className="text-[#52635C]">Cargando…</div> : personal.length === 0 ? (
            <div className="card-warm p-10 text-center text-[#52635C]">Aún no tenés libros propios cargados.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {personal.map((it) => <ItemCard key={it.id} item={it} onDelete={removeItem} />)}
            </div>
          )}
        </TabsContent>

        {/* LINKS */}
        <TabsContent value="links" className="mt-5">
          <p className="text-sm text-[#52635C] mb-4">Recursos externos gratuitos y confiables, de línea reformada o conservadora.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {links.map((l) => (
              <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="card-warm p-5 block hover:no-underline" data-testid={`link-${l.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <ExternalLink size={14} className="text-[#E2B85C]" />
                  <h3 className="font-serif text-lg font-semibold text-[#13302A]">{l.title}</h3>
                </div>
                <p className="text-sm text-[#52635C]">{l.description}</p>
                <div className="text-xs font-mono text-[#1F4A40] mt-2 truncate">{l.url}</div>
              </a>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
